import { FilePreviewConfig } from './file-preview.interface';

export enum EnrollmentStatus {
    APPROVED = 'APPROVED',
    REJECTED = 'REJECTED',
    REFUND = 'REFUND',
    REGISTERED = 'REGISTERED',
    ONGOING = 'ONGOING',
    PAYMENT_PROCESSING = 'PAYMENT_PROCESSING',
    COMPLETED = 'COMPLETED',
    PAYMENT_FAILED = 'PAYMENT_FAILED',
    IN_REVIEW = 'REVIEW',
    REFUND_ISSUED = 'REFUND_ISSUED',
}

export enum ProgramType {
    OPEN = 'OPEN',
    IN_HOUSE = 'IN_HOUSE',
    CLOSED = 'CLOSED',
    NPTL = 'NPTL',
}

export interface UpdateEnrollmentStatusRequest {
    enrollmentIds: string[];
    newStatus: EnrollmentStatus;
    transactionId?: string;
}

export interface InitiateRefundRequest {
    enrollmentId?: string;
    refundAmount: number;
    refundDate: string;
    refundType: string;
    comment?: string;
}

export interface InitiateRefundResponse {
    data: string;
    totalRecords: number;
    errors: {};
    hasNext: boolean;
}
export interface UpdateEnrollmentStatusResponse {
    data: string | null;
    totalRecords: number;
    errors: ApiError[] | null;
    hasNext: boolean;
}

export interface CourseRegistration {
    courseId: string;
    courseCode: string | null;
    courseName: string;
    programType: ProgramType;
    maxEnrollment: number;
    applications: number;
    inReviewApplications: number;
    approvedApplications: number;
    registeredApplications: number;
    rejectedApplications: number;
    isEligibleForApproval: boolean;
    courseStatus: string;
    ownerFullName: string;
    canDownload: boolean;
}

export interface ApiError {
    errorCode: number;
    message: string;
}

export interface CourseRegistrationResponse {
    data: CourseRegistration[];
    totalRecords: number;
    hasNext: boolean;
    errors: ApiError[];
}

export interface CourseEnrollment {
    enrollmentId: string;
    fullName: string;
    studentEmail: string;
    lastCommentedBy: string;
    lastCommentTime: string;
    enrollmentType: string;
    highestEducationalQualification: string;
    currentOrganisation: string;
    designation: string;
    experience: string;
    fees?: number | null;
    status: EnrollmentStatus;
    document?: string | null;
    enrollmentDocumentPath?: string | null;
    accommodationRequired?: boolean;
}

export interface CourseDetailsResponse {
    data: {
        maxEnrollment: number;
        totalEnrollment: number;
        totalRegisteredCount: number;
        courseName: string;
        courseId: string;
        courseCode: string | null;
        programName: ProgramType;
        facultyNames?: string;
        enrollments: CourseEnrollment[];
    };
    hasNext: boolean;
    totalRecords: number;
    errors: ApiError[];
}

export interface ApplicantDetailsData {
    courseName: string;
    fullName: string;
    email: string;
    gender: string;
    mobileNumber: string;
    currentOrganisation: string;
    designation: string;
    experience: string;
    highestEducationalQualification: string;
    enrollmentType: string;
    mailingAddress: string;
    accommodationRequired: boolean;
    enrollmentStatus: EnrollmentStatus;
    paymentStatus: string;
    fees: number;
    dateOfPayment: string;
    paymentMode: string;
    transactionId: string;
    enrollmentPersonalId1: FileData | null;
    enrollmentPersonalId2: FileData | null;
    enrollmentMarksheet: FileData | null;
    enrollmentDocument: FileData | null;
}

export interface ApplicantDetailsResponse {
    data: ApplicantDetailsData;
    totalRecords: number;
    errors: ApiError[];
}

interface ApplicantBase {
    name: string;
    email: string;
    gender?: string;
    mobileNumber?: string;
    mailingAddress: string;
    accommodation?: string;
    accommodationRequired?: boolean;
    education: {
        qualification: string;
        organization: string;
        designation: string;
        experience: string;
    };
    feeCategory: string;
}

export interface FileData {
    fileName: string;
    fileSize: number;
    id: string;
    url: string;
}

export interface Applicant extends ApplicantBase {
    enrollmentId: string;
    documents?: {
        personalIdOne?: {
            label: string;
            fileData: FilePreviewConfig | null;
        };
        personalIdTwo?: {
            label: string;
            fileData: FilePreviewConfig | null;
        };
        marksheet?: {
            label: string;
            fileData: FilePreviewConfig | null;
        };
        other?: {
            label: string;
            fileData: FilePreviewConfig | null;
        };
    };
    status: EnrollmentStatus;
    lastModified: {
        by: string;
        date: string;
        time: string;
    } | null;
    canSendPaymentReminder?: boolean;
    paymentDetails?: {
        amount: number;
        paidOn: string;
    };
    feeDetails?: {
        paymentStatus: string;
        feeAmount: number;
        dateOfPayment: string;
        paymentMode: string;
        transactionId: string;
    };
    fees?: number | null;
    document?: string | null;
    isExpanded?: boolean;
    courseName?: string;
}

export interface Comment {
    id: string;
    comment: string;
    commenterName: string;
    commenterDesignation: string;
    commenterRole: string;
    createdDateTime: string;
}

export interface CommentsResponse {
    data: Comment[];
    totalRecords: number;
    errors: ApiError[];
}

export interface CreateCommentRequest {
    enrollmentId: string;
    comment: string;
}

export interface CreateCommentResponse {
    success: boolean;
    errors: ApiError[];
}

export interface SendPaymentReminderRequest {
    ids: string[];
    templateFileName: string;
    idType: string;
}

export interface SendEmailToApplicantsRequest {
    ids: string[];
    subject: string;
    body: string;
    idType: string;
}

export interface EmailData {
    subject: string;
    message: string;
}

export interface SearchableApplicantsConfig {
    courseId: string;
    placeholder?: string;
    maxHeight?: string;
    width?: string;
}

export interface SearchableApplicantsRequest {
    email: string;
    enrollmentId: string;
    enrollmentType: string;
    fullName: string;
    status: EnrollmentStatus;
}

export interface SearchableApplicantsResponse {
    data: SearchableApplicantsRequest[];
    totalRecords: number;
    hasNext: boolean;
    errors: ApiError[];
}

export interface QualificationResponse {
    data: string[];
    totalRecords: number;
    hasNext: boolean;
    errors: ApiError[];
}

export interface InHouseApplicant {
    enrollmentId: string;
    fullName: string;
    mobile: string;
    email: string;
    empId: string;
}

export interface InHouseApplicantsResponse {
    data: InHouseApplicant[];
    totalRecords: number;
    hasNext: boolean;
    errors?: ApiError[];
}
